import * as React from 'react'
import Chip from '@mui/material/Chip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import { useTheme } from '@mui/material/styles'
import { toast } from '@sales-center/components'
import { CALENDAR_STATUS_COLORS } from '@sales-center/common'
// Imported from the module, not the `effects` barrel: the barrel re-exports
// the bulk effects, which import the Activity feature root and pull the whole
// store graph in behind this leaf component.
import { writeActivityStatus } from '@user/features/Activity/effects/updateStatus.effect'
import {
  ActivityStatus,
  ActivityStatusOptions,
} from '@user/features/Activity/constants'

type Props = {
  activityId: number
  status: ActivityStatus
  /** Lifts a committed status out of the chip so reopening the popover in the
   *  same session reads the written value, not the stale one from the feed. */
  onCommitted?: (status: ActivityStatus) => void
}

const STATUS_LABEL: Record<ActivityStatus, string> =
  ActivityStatusOptions.reduce(
    (acc, option) => {
      acc[option.value as ActivityStatus] = option.label
      return acc
    },
    {} as Record<ActivityStatus, string>,
  )

/**
 * Editable activity status (SH-1986, SPEC-EventDetailPopover §3). An outlined
 * chip whose colour comes from the theme's `calendarStatus` group; clicking it
 * opens a menu of the four statuses. The write is optimistic — the chip updates
 * immediately, then rolls back with an error toast if the PUT fails (E4 / Q17).
 * Owner-only events, so there is no permission branch: it is always editable.
 */
export function StatusChip({
  activityId,
  status: initialStatus,
  onCommitted,
}: Props) {
  const theme = useTheme()
  const [status, setStatus] = React.useState(initialStatus)
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [saving, setSaving] = React.useState(false)

  // The chip lives inside the Popover, which unmounts on close; guard state
  // updates from a write that resolves after the user has closed the popover.
  const mounted = React.useRef(true)
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // The Tidal theme does not carry the calendar palettes, so fall back to the
  // shared tokens rather than throwing — same guard `resolveEventColor` uses.
  const colors =
    theme.palette.calendarStatus?.[status] ?? CALENDAR_STATUS_COLORS[status]

  const select = async (next: ActivityStatus) => {
    setAnchorEl(null)
    // Ignore re-entry while a write is in flight so two picks can't fire
    // overlapping PUTs whose out-of-order responses clobber each other.
    if (saving || next === status) return
    const previous = status
    setStatus(next) // optimistic
    setSaving(true)
    try {
      // The shared writer owns the failure toast so that every caller of this
      // endpoint reports identically — hence a rollback with no toast here.
      const result = await writeActivityStatus({ id: activityId, status: next })
      if (!result.ok) {
        if (mounted.current) setStatus(previous) // rollback
        return
      }
      toast.success(`Status set to ${STATUS_LABEL[next]}`)
      // The page handles what a commit means beyond this chip (refreshing the
      // app-wide overdue count, remembering the value for a reopen) — the same
      // split the Activity panel uses, where its handler owns those and the
      // field does not.
      onCommitted?.(next)
    } catch {
      // Only reached if the request itself threw — nothing has toasted yet.
      if (mounted.current) setStatus(previous) // rollback
      toast.error('Failed to update status')
    } finally {
      if (mounted.current) setSaving(false)
    }
  }

  return (
    <>
      <Chip
        label={STATUS_LABEL[status]}
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onDelete={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
        deleteIcon={<KeyboardArrowDown />}
        data-test-id="activity-status-chip"
        aria-label={`Status: ${STATUS_LABEL[status]}. Change status`}
        sx={{
          height: 26,
          fontWeight: 600,
          color: colors.text,
          backgroundColor: colors.background,
          borderColor: colors.border,
          '& .MuiChip-deleteIcon': { color: colors.text },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 184 } }}
      >
        {Object.values(ActivityStatus).map((value) => (
          <MenuItem
            key={value}
            selected={value === status}
            onClick={() => void select(value)}
            sx={{ fontSize: 14 }}
          >
            {STATUS_LABEL[value]}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
